import { vehicleZeichenSpec, renderZeichen, scaledSize } from './taktischeZeichen';

// Führungsstruktur eines Szenarios und daraus erzeugte Diagramme (Führungsharke, Funkskizze).
// Gilt nur für das jeweilige Szenario; die allgemeine Fahrzeugliste bleibt unverändert.

export const EL_ID = 'el';

export const createDefaultOrganisation = () => ({
  einsatzleitung: { name: 'Einsatzleitung', fuehrer: '', funkrufname: '', gruppe: '' },
  abschnitte: [],
});

const str = (value, max = 120) => (typeof value === 'string' ? value.substring(0, max) : '');

// Bereinigt eine (importierte) Führungsstruktur
export const normalizeOrganisation = (org) => {
  const base = createDefaultOrganisation();
  if (!org || typeof org !== 'object') return base;
  const el = org.einsatzleitung || {};
  return {
    einsatzleitung: {
      name: str(el.name) || base.einsatzleitung.name,
      fuehrer: str(el.fuehrer),
      funkrufname: str(el.funkrufname),
      gruppe: str(el.gruppe),
    },
    abschnitte: (Array.isArray(org.abschnitte) ? org.abschnitte : [])
      .filter(a => a && typeof a.id === 'string')
      .slice(0, 50)
      .map(a => ({
        id: a.id.substring(0, 64),
        name: str(a.name) || 'Einsatzabschnitt',
        fuehrer: str(a.fuehrer),
        funkrufname: str(a.funkrufname),
        gruppe: str(a.gruppe),
      })),
  };
};

// Anzeigename eines Fahrzeugs im Szenario
export const vehicleDisplayName = (properties = {}) => properties.customName || properties.name || 'Fahrzeug';

// ==================== SVG-ERZEUGUNG ====================

const escapeXml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const FONT = 'font-family="Arial, Helvetica, sans-serif"';
const COL_WIDTH = 230;
const COL_GAP = 24;
const UNIT_HEIGHT = 50;
const MARGIN = 30;

const text = (x, y, value, { size = 12, weight = 400, anchor = 'start', fill = '#111827' } = {}) =>
  value ? `<text x="${x}" y="${y}" ${FONT} font-size="${size}" font-weight="${weight}" text-anchor="${anchor}" fill="${fill}">${escapeXml(value)}</text>` : '';

const image = (zeichen, x, y, height) => {
  if (!zeichen) return { svg: '', width: 0 };
  const [w, h] = scaledSize(zeichen.size, height);
  return { svg: `<image href="${zeichen.url}" x="${x}" y="${y}" width="${w}" height="${h}"/>`, width: w };
};

const elZeichen = () => renderZeichen({ grundzeichen: 'befehlsstelle', organisation: 'feuerwehr', text: 'EL' });
const abschnittZeichen = () => renderZeichen({ grundzeichen: 'befehlsstelle', organisation: 'feuerwehr', text: 'EA' });

// Knoten für Einsatzleitung bzw. Abschnitt (zentriert um cx)
const leadNode = (cx, y, zeichen, title, lines) => {
  const width = COL_WIDTH - 10;
  const x = cx - width / 2;
  const img = image(zeichen, x + 10, y + 10, 34);
  const tx = x + 20 + img.width;
  return `
    <rect x="${x}" y="${y}" width="${width}" height="56" rx="6" fill="#ffffff" stroke="#111827" stroke-width="1.5"/>
    ${img.svg}
    ${text(tx, y + 22, title, { size: 13, weight: 700 })}
    ${lines.filter(Boolean).slice(0, 2).map((line, i) => text(tx, y + 38 + i * 13, line, { size: 11, fill: '#374151' })).join('')}`;
};

const unitRow = (x, y, unit) => {
  const img = image(renderZeichen(vehicleZeichenSpec(unit.properties)), x, y + 6, 28);
  const tx = x + img.width + 8;
  const callsign = unit.properties.callsign;
  return `
    ${img.svg}
    ${text(tx, y + 18, callsign || vehicleDisplayName(unit.properties), { size: 12, weight: 700 })}
    ${callsign ? text(tx, y + 32, vehicleDisplayName(unit.properties), { size: 11, fill: '#374151' }) : ''}`;
};

const footer = (width, y, title, unassigned) => {
  const note = unassigned.length
    ? `Nicht zugeordnet: ${unassigned.map(u => u.properties.callsign || vehicleDisplayName(u.properties)).join(', ')}`
    : '';
  return `
    ${text(MARGIN, y, note, { size: 11, fill: '#b91c1c' })}
    ${text(width - MARGIN, y + (note ? 18 : 0), `${title} · erstellt mit Luenefire | FPS · ${new Date().toLocaleString('de-DE')}`, { size: 10, anchor: 'end', fill: '#6b7280' })}`;
};

// Spalten: Abschnitte plus "direkt der EL unterstellt", jeweils mit zugeordneten Fahrzeugen
const buildColumns = (organisation, vehicles) => {
  const byAbschnitt = (id) => vehicles.filter(v => v.properties.abschnittId === id);
  const columns = organisation.abschnitte.map(a => ({ ...a, units: byAbschnitt(a.id), isAbschnitt: true }));
  const direct = byAbschnitt(EL_ID);
  if (direct.length) {
    columns.unshift({ id: EL_ID, name: 'Direkt der EL unterstellt', units: direct, isAbschnitt: false });
  }
  const knownIds = new Set([EL_ID, ...organisation.abschnitte.map(a => a.id)]);
  const unassigned = vehicles.filter(v => !knownIds.has(v.properties.abschnittId));
  return { columns, unassigned };
};

const svgDocument = (width, height, body) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
    <rect width="100%" height="100%" fill="#ffffff"/>${body}</svg>`;

export const buildFuehrungsharke = ({ organisation, vehicles, title = 'Führungsharke' }) => {
  const { columns, unassigned } = buildColumns(organisation, vehicles);
  const colCount = Math.max(columns.length, 1);
  const width = Math.max(MARGIN * 2 + colCount * COL_WIDTH + (colCount - 1) * COL_GAP, 520);
  const maxUnits = Math.max(0, ...columns.map(c => c.units.length));
  const elY = 50;
  const railY = elY + 56 + 30;
  const colTop = railY + 24;
  const unitsTop = colTop + 56 + 16;
  const height = unitsTop + maxUnits * UNIT_HEIGHT + 70;
  const el = organisation.einsatzleitung;

  const colX = (i) => MARGIN + i * (COL_WIDTH + COL_GAP) + COL_WIDTH / 2;
  let body = text(MARGIN, 30, title, { size: 18, weight: 700 });
  body += leadNode(width / 2, elY, elZeichen(), el.name || 'Einsatzleitung', [el.fuehrer, el.funkrufname]);

  if (columns.length) {
    // Harke: senkrechte Linie von der EL zum Querbalken, dann Zinken zu den Abschnitten
    body += `<g stroke="#111827" stroke-width="2" fill="none">
      <line x1="${width / 2}" y1="${elY + 56}" x2="${width / 2}" y2="${railY}"/>
      <line x1="${colX(0)}" y1="${railY}" x2="${colX(columns.length - 1)}" y2="${railY}"/>
      ${columns.map((_, i) => `<line x1="${colX(i)}" y1="${railY}" x2="${colX(i)}" y2="${colTop}"/>`).join('')}
    </g>`;
  }

  columns.forEach((col, i) => {
    const cx = colX(i);
    body += leadNode(cx, colTop, col.isAbschnitt ? abschnittZeichen() : elZeichen(), col.name,
      col.isAbschnitt ? [col.fuehrer, col.funkrufname] : []);
    if (col.units.length) {
      const lineX = cx - COL_WIDTH / 2 + 14;
      const lastY = unitsTop + (col.units.length - 1) * UNIT_HEIGHT + 20;
      body += `<line x1="${lineX}" y1="${colTop + 56}" x2="${lineX}" y2="${lastY}" stroke="#111827" stroke-width="1.5"/>`;
      col.units.forEach((unit, j) => {
        const y = unitsTop + j * UNIT_HEIGHT;
        body += `<line x1="${lineX}" y1="${y + 20}" x2="${lineX + 12}" y2="${y + 20}" stroke="#111827" stroke-width="1.5"/>`;
        body += unitRow(lineX + 16, y, unit);
      });
    }
  });

  if (!columns.length) {
    body += text(width / 2, colTop + 30, 'Noch keine Einsatzabschnitte oder zugeordneten Fahrzeuge', { size: 12, anchor: 'middle', fill: '#6b7280' });
  }

  body += footer(width, height - 30, title, unassigned);
  return { svg: svgDocument(width, height, body), width, height };
};

// Funkverkehrskreis als gestrichelter Rahmen mit Gruppenbezeichnung
const kreis = (x, y, w, h, label) => `
  <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="14" fill="#eff6ff" stroke="#2563eb" stroke-width="1.5" stroke-dasharray="6,4"/>
  ${text(x + 12, y + 16, label, { size: 11, weight: 700, fill: '#1d4ed8' })}`;

export const buildFunkskizze = ({ organisation, vehicles, title = 'Funkskizze' }) => {
  const { columns, unassigned } = buildColumns(organisation, vehicles);
  const colCount = Math.max(columns.length, 1);
  const width = Math.max(MARGIN * 2 + colCount * COL_WIDTH + (colCount - 1) * COL_GAP, 520);
  const maxUnits = Math.max(0, ...columns.map(c => c.units.length));
  const el = organisation.einsatzleitung;
  const colX = (i) => MARGIN + i * (COL_WIDTH + COL_GAP) + COL_WIDTH / 2;

  // Führungskreis: EL und alle Abschnittsführer
  const fkTop = 50;
  const elY = fkTop + 26;
  const leadersY = elY + 56 + 36;
  const fkHeight = (columns.length ? leadersY + 56 : elY + 56) - fkTop + 16;
  const unitsKreisTop = fkTop + fkHeight + 40;
  const unitsKreisHeight = maxUnits * UNIT_HEIGHT + 34;
  const height = unitsKreisTop + (maxUnits ? unitsKreisHeight : 0) + 70;

  let body = text(MARGIN, 30, title, { size: 18, weight: 700 });
  body += kreis(MARGIN - 10, fkTop, width - 2 * (MARGIN - 10), fkHeight,
    `Führungskreis${el.gruppe ? ` – ${el.gruppe}` : ''}`);

  if (columns.length) {
    body += `<g stroke="#2563eb" stroke-width="1.5" fill="none">${columns.map((_, i) =>
      `<line x1="${width / 2}" y1="${elY + 56}" x2="${colX(i)}" y2="${leadersY}"/>`).join('')}</g>`;
  }
  body += leadNode(width / 2, elY, elZeichen(), el.funkrufname || el.name || 'Einsatzleitung',
    [el.funkrufname ? el.name : '', el.fuehrer]);

  columns.forEach((col, i) => {
    const cx = colX(i);
    body += leadNode(cx, leadersY, col.isAbschnitt ? abschnittZeichen() : elZeichen(),
      col.isAbschnitt ? (col.funkrufname || col.name) : col.name,
      col.isAbschnitt ? [col.funkrufname ? col.name : '', col.fuehrer] : []);
    if (!col.units.length) return;

    // Abschnittskreis: Abschnittsführer und zugeordnete Fahrzeuge
    const groupLabel = col.isAbschnitt ? (col.gruppe || 'Gruppe offen') : (el.gruppe || 'Gruppe offen');
    const kx = cx - COL_WIDTH / 2;
    body += `<line x1="${cx}" y1="${leadersY + 56}" x2="${cx}" y2="${unitsKreisTop}" stroke="#2563eb" stroke-width="1.5"/>`;
    body += kreis(kx, unitsKreisTop, COL_WIDTH, col.units.length * UNIT_HEIGHT + 34, groupLabel);
    col.units.forEach((unit, j) => {
      body += unitRow(kx + 14, unitsKreisTop + 26 + j * UNIT_HEIGHT, unit);
    });
  });

  body += footer(width, height - 30, title, unassigned);
  return { svg: svgDocument(width, height, body), width, height };
};

// ==================== EXPORT ====================

const download = (url, filename) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
};

export const downloadSvg = ({ svg }, filename) => {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  download(url, `${filename}.svg`);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const downloadPng = ({ svg, width, height }, filename, scale = 2) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    download(canvas.toDataURL('image/png'), `${filename}.png`);
    resolve();
  };
  img.onerror = (error) => {
    URL.revokeObjectURL(url);
    reject(error);
  };
  img.src = url;
});

// Druck über ein unsichtbares iframe (kein Popup-Blocker)
export const printSvg = ({ svg }, title) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeXml(title)}</title>
    <style>@page{size:landscape;margin:10mm}body{margin:0}svg{max-width:100%;height:auto}</style></head>
    <body>${svg}</body></html>`);
  doc.close();
  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => iframe.remove(), 1000);
  }, 300);
};
